import React, { useState, useEffect, useRef, useContext, useMemo, useCallback } from 'react';
import CreateCloudfrontUrl from '/src/services/CreateCloudfrontUrl';
import RiskOverviewClimateIcon from '/src/Components/RiskOverviewClimateIcon/RiskOverviewClimateIcon';
import { WindowSizeContext } from '/src/App';
import { ReactFlow, Handle, Position, BezierEdge, SmoothStepEdge, StepEdge, useEdges } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { CurlyBraceDivider } from '/src/Components/CurlyBraceDivider/CurlyBraceDivider';

import './style.css';

const RisksToHomeRelationships = ({
    propertyData,
    climateRisks,
    houseAreasThreatened,
}) => {
    const { width, height, deviceType } = useContext(WindowSizeContext);

    const new_hub_house_image_filename = "house-lansdcape.webp";
    const new_hub_house_image_url = CreateCloudfrontUrl(new_hub_house_image_filename);

    // Set the states for when a user hovers over a climate risk or a house area
    const [hoveredClimateRisk, setHoveredClimateRisk] = useState(climateRisks[0]);
    const [hoveredHouseArea, setHoveredHouseArea] = useState(null);

    const [showFlow, setShowFlow] = useState(false);
    const [flowReady, setFlowReady] = useState(false);
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);

    // Set up the handlers for when a user hovers over a climate risk or a house area and then why they leave the hover
    const handleClimateRiskHover = (risk) => {
        // console.log(risk);
        setHoveredClimateRisk(risk);
    };

    const handleClimateRiskLeave = () => {
        setHoveredClimateRisk(null);
    };

    const handleHouseAreaHover = (area) => {
        // console.log(area);
        setHoveredHouseArea(area);
    };

    const handleHouseAreaLeave = () => {
        setHoveredHouseArea(null);
    };

    // Map numeric IDs to house area classes based on the actual data
    const getAreaClass = (id) => {
        const areaMap = {
            '2': 'roof',           // First object in array has id '2'
            '1': 'landscaping',    // Second object has id '1'
            '6': 'windows',        // Third object has id '6'
            '10': 'hvac',          // Fourth object has id '10'
            '7': 'doors',          // Fifth object has id '7'
            '14': 'frame',         // Sixth object has id '14'
            '4': 'foundation',     // Seventh object has id '4'
            '9': 'siding',         // Eighth object has id '9'
            '5': 'plumbing',       // Ninth object has id '5'
            '13': 'basement'       // Tenth object has id '13'
        };
        return areaMap[id] || 'unknown';
    };

    // Function to get element position relative to the container
    const getElementPosition = useCallback((element, nodeType) => {
        const container = document.querySelector('.risks-to-home-relationships-container');
        if (!element || !container) {
            console.warn('Missing element or container:', { element, container, nodeType });
            return { x: 0, y: 0 };
        }

        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        // Get the base position at the center of the element
        let x = elementRect.left - containerRect.left;
        let y = elementRect.top - containerRect.top;

        // For risk nodes, position at the bottom center of the icon
        if (nodeType === 'riskNode') {
            x += elementRect.width / 2;
            y += elementRect.height;
        }
        // For house markers, position at the center of the marker
        else if (nodeType === 'houseMarkerNode') {
            x += elementRect.width / 2;
            y += elementRect.height / 2;
        }

        return { x, y };
    }, []);

    const nodeTypes = useMemo(() => ({
        riskNode: RiskNode,
        houseMarkerNode: HouseMarkerNode,
    }), []);

    const edgeTypes = useMemo(() => ({
        smoothstep: SmoothStepEdge,
        bezier: BezierEdge
    }), []);

    const getNodesAndEdges = () => {
        const nodes = [];

        // Create nodes for each climate risk
        climateRisks.forEach(risk => {
            const element = document.getElementById(`risk-${risk.risk_id}`);
            const position = getElementPosition(element, 'riskNode');
            nodes.push({
                id: `risk-${risk.risk_id}`,
                type: 'riskNode',
                position,
                data: { label: risk.risk_name } // Adding label for debugging
            });
        });

        // Create nodes for each house area
        houseAreasThreatened.forEach(area => {
            const element = document.getElementById(`area-${area.id}`);
            const position = getElementPosition(element, 'houseMarkerNode');
            nodes.push({
                id: `area-${area.id}`,
                type: 'houseMarkerNode',
                position,
                data: { label: area.category } // Adding label for debugging
            });
        });

        // Edges
        const edges = [];

        if ((nodes.length > 0 && (hoveredClimateRisk || hoveredHouseArea))) {

            if (hoveredClimateRisk) {
                // Find the climate risk node for source
                const sourceNode = `risk-${hoveredClimateRisk.risk_id}`;

                // Find the house areas where hoveredClimateRisk included the hoveredClimateRisk.risk_id
                const applicableHouseAreas = houseAreasThreatened.filter(area => area.applicable_risks.includes(hoveredClimateRisk.risk_id));

                // Create edges for each applicable house area
                applicableHouseAreas.forEach(area => {
                    edges.push({
                        id: `edge-${sourceNode}-${area.id}`,
                        source: sourceNode,
                        target: `area-${area.id}`,
                    });
                });

            }
            if (hoveredHouseArea) {
                // Find the house area node for source
                const sourceNode = nodes.find(node => node.id === `area-${hoveredHouseArea.id}`);

                // Find the climate risks where hoveredHouseArea included the hoveredHouseArea.risk_id
                // const applicableClimateRisks = climateRisks.filter(risk => hoveredHouseArea.applicable_risks.includes(risk.risk_id));
                console.log(hoveredHouseArea);
                const applicableClimateRisks = hoveredHouseArea.applicable_risks;

                // Create edges for each applicable climate risk
                applicableClimateRisks.forEach(risk => {
                    edges.push({
                        id: `edge-${sourceNode.id}-${risk}`,
                        source: `risk-${risk}`,
                        target: sourceNode.id,
                    });
                });
            }

            // Update every edge with styling
            edges.forEach(edge => {
                edge.style = {
                    stroke: 'var(--dark-grey',
                    strokeWidth: 2,
                    strokeLinecap: 'round',
                    animation: "dashdraw 0.5s linear infinite", // Adjust the duration here
                    strokeDasharray: 5,
                    zIndex: -1
                };
            });
        }

        return { nodes, edges };
    }

    useEffect(() => {
        setFlowReady(false);
        const timer = setTimeout(() => {
            const { nodes, edges } = getNodesAndEdges();
            if (nodes.length > 0) {
                setNodes(nodes);
                // console.log('Nodes created:', nodes);
            }
            if (edges.length > 0) {
                setEdges(edges);
                // console.log('Edges created:', edges);
                setFlowReady(true);
            }
        }, 100);

        return () => clearTimeout(timer);
    }, [hoveredClimateRisk, hoveredHouseArea, climateRisks, houseAreasThreatened, width, height]);


    // Tiny component that signals when DOM is ready
    const FlowTrigger = ({ onMount }) => {
        useEffect(() => {
            onMount();
        }, []);
        return <div style={{ display: 'none' }} />;
    };

    let hoverOverTextStarter = "Hover over"
    if (deviceType === 'mobile' || deviceType === 'tablet') { 
        hoverOverTextStarter = "Tap on"
    }
    
    const hoverOverText = `${hoverOverTextStarter} a risk or different part of the home to see the relationship between climate threats and physical damage.`;

    return (
        <div className="risks-to-home-relationships-frame">
            {/* <h4>Risks to Home Relationships</h4> */}
            <div className={`risks-to-home-relationships-container ${deviceType}`}>
                {showFlow && flowReady && (
                    <div className="reactflow-wrapper">
                        <ReactFlow
                            nodes={nodes}
                            edges={edges}
                            nodeTypes={nodeTypes}
                            edgeTypes={edgeTypes}
                            fitView={false}
                            nodesDraggable={false}
                            nodesConnectable={false}
                            elementsSelectable={false}
                            panOnScroll={false}
                            zoomOnScroll={false}
                            panOnDrag={false}
                            minZoom={1}
                            maxZoom={1}
                            defaultViewport={{ x: 0, y: 0, zoom: 1 }}
                            style={{ width: '100%', height: '100%' }}
                        >
                            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />
                        </ReactFlow>
                    </div>
                )}
                <div className={`risks-to-home-climate-risks-container ${deviceType}`}>
                    {climateRisks.map((risk) => {

                        let notSelected = null;
                        if ((hoveredClimateRisk && hoveredClimateRisk.risk_id !== risk.risk_id) || (hoveredHouseArea && !hoveredHouseArea.applicable_risks.includes(risk.risk_id))) {
                            notSelected = "not-selected";
                            // console.log(notSelected)
                        }

                        const areThereEdges = edges.length > 0;
                        const edge = edges.find(edge => edge.source === `risk-${risk.id}`);
                        let cssAdder = '';
                        if (hoveredClimateRisk && hoveredClimateRisk.risk_id !== risk.risk_id) {
                            cssAdder = 'opaque';
                        } else if (hoveredHouseArea && !hoveredHouseArea.applicable_risks.includes(risk.risk_id)) {
                            cssAdder = 'opaque';
                        }

                        return (
                            <div
                                className={`risks-to-home-climate-risk-item ${cssAdder}`}
                                key={risk.risk_id}
                                id={`risk-${risk.risk_id}`}
                                onMouseEnter={() => handleClimateRiskHover(risk)}
                                onMouseLeave={handleClimateRiskLeave}
                            >
                                <RiskOverviewClimateIcon
                                    id={risk.id}
                                    risk_level={risk.risk_level}
                                    icon_white={risk.icon_white}
                                    icon_black={risk.icon_black}
                                    risk_name={risk.risk_name}
                                    requiresLookup={false}
                                    displayName={false}
                                />
                            </div>
                        )
                    })}
                </div>
                <div className="risks-to-home-house-areas-container">
                    <div className="risks-to-home-relationships-home-hub-container">
                        <img
                            className="climate-factors-home-hub-image"
                            src={new_hub_house_image_url}
                            alt="House diagram"
                        />
                        <div className="house-risk-markers">
                            {houseAreasThreatened.map((area) => {

                                let notSelected;
                                if ((hoveredHouseArea && hoveredHouseArea.id !== area.id) || (hoveredClimateRisk && !area.applicable_risks.includes(hoveredClimateRisk.risk_id))) {
                                    notSelected = "not-selected";
                                }

                                return (
                                    <div
                                        id={`area-${area.id}`}
                                        key={area.id}
                                        onMouseEnter={() => handleHouseAreaHover(area)}
                                        onMouseLeave={handleHouseAreaLeave}
                                        className={`house-risk-marker house-risk-marker-${getAreaClass(area.id)} ${area.risk_level_adjusted} ${notSelected} ${deviceType}`}
                                    // style={{
                                    //     backgroundColor: getRiskColor(area.risk_level_adjusted),
                                    // }}
                                    >
                                        {area.category}
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>
            </div>
            <p className="text-align-center hover-over-area-text">{hoverOverText}</p>
            <CurlyBraceDivider direction="down" color="grey" />

            <FlowTrigger onMount={() => setShowFlow(true)} />
        </div>
    );
};

export default RisksToHomeRelationships;

// Custom node components - just handles the positioning of the nodes
const RiskNode = () => {
    return (
        <div style={{ width: 1, height: 1, position: 'relative' }}>
            <Handle
                type="source"
                position={Position.Bottom}
                style={{
                    visibility: 'visible',
                    bottom: -10,
                    background: 'transparent',
                    border: 'none'
                }}
            />
        </div>
    );
};

const HouseMarkerNode = () => {
    return (
        <div style={{ width: 1, height: 1, position: 'relative' }}>
            <Handle
                type="target"
                position={Position.Top}
                style={{
                    visibility: 'visible',
                    top: -10,
                    background: 'transparent',
                    border: 'none'
                }}
            />
        </div>
    );
};